import type { MediaItem } from "./types";

export const ZIP_MAX_FILES = 10;
export const ZIP_MAX_BYTES = 100 * 1024 * 1024;

export type ZipFailure = "ZIP_TOO_MANY" | "ZIP_TOO_LARGE" | "ZIP_CORS";

export class ZipError extends Error {
  code: ZipFailure;

  constructor(code: ZipFailure) {
    super(code);
    this.name = "ZipError";
    this.code = code;
  }
}

export interface ZipBuildProgress {
  phase: "downloading" | "zipping";
  completed: number;
  total: number;
}

export function mediaZipBatches(items: MediaItem[]): MediaItem[][] {
  const batches: MediaItem[][] = [];
  for (let index = 0; index < items.length; index += ZIP_MAX_FILES) {
    batches.push(items.slice(index, index + ZIP_MAX_FILES));
  }
  return batches;
}

export async function buildMediaZip(items: MediaItem[], options: { skipSizePreflight?: boolean; maxBytes?: number; validateURL?: (url: string) => boolean; redirect?: RequestRedirect; onProgress?: (progress: ZipBuildProgress) => void } = {}): Promise<Blob> {
  const maxBytes = options.maxBytes ?? ZIP_MAX_BYTES;
  const declaredSizes = options.skipSizePreflight ? [] : await declaredMediaSizes(items, 3, options.validateURL, options.redirect);
  if (!options.skipSizePreflight && declaredSizes.reduce((total, size) => total + Math.max(0, size), 0) > maxBytes) {
    throw new ZipError("ZIP_TOO_LARGE");
  }

  const files: Record<string, Uint8Array> = {};
  const filenames = new Map<string, number>();
  let total = 0;
  options.onProgress?.({ phase: "downloading", completed: 0, total: items.length });
  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    let response: Response;
    try {
      response = await fetch(item.url, { mode: "cors", credentials: "omit", referrerPolicy: "no-referrer", redirect: options.redirect || "follow" });
    } catch {
      throw new ZipError("ZIP_CORS");
    }
    if (!response.ok || response.type === "opaque" || (options.validateURL && !options.validateURL(response.url || item.url))) throw new ZipError("ZIP_CORS");
    const declared = Number(response.headers.get("content-length") || 0);
    if (declared > 0 && total + declared > maxBytes) throw new ZipError("ZIP_TOO_LARGE");
    const buffer = new Uint8Array(await response.arrayBuffer());
    total += buffer.byteLength;
    if (total > maxBytes) throw new ZipError("ZIP_TOO_LARGE");
    files[uniqueFilename(item.filename, filenames)] = buffer;
    options.onProgress?.({ phase: "downloading", completed: index + 1, total: items.length });
  }
  options.onProgress?.({ phase: "zipping", completed: items.length, total: items.length });
  if (options.onProgress) await new Promise<void>((resolve) => setTimeout(resolve, 0));
  const { zipSync } = await import("fflate");
  return new Blob([zipSync(files, { level: 0 })], { type: "application/zip" });
}

async function declaredMediaSizes(items: MediaItem[], concurrency: number, validateURL?: (url: string) => boolean, redirect: RequestRedirect = "follow"): Promise<number[]> {
  return mapWithConcurrency(items, concurrency, async (item) => {
    try {
      const response = await fetch(item.url, { method: "HEAD", mode: "cors", credentials: "omit", referrerPolicy: "no-referrer", redirect });
      if (!response.ok || response.type === "opaque" || (validateURL && !validateURL(response.url || item.url))) return 0;
      return Number(response.headers.get("content-length") || 0);
    } catch {
      return 0;
    }
  });
}

async function mapWithConcurrency<T, R>(items: T[], limit: number, mapper: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  async function worker() {
    while (cursor < items.length) {
      const index = cursor++;
      results[index] = await mapper(items[index]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
  return results;
}

function uniqueFilename(filename: string, seen: Map<string, number>): string {
  const count = (seen.get(filename) || 0) + 1;
  seen.set(filename, count);
  if (count === 1) return filename;
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? `${filename.slice(0, dot)}-${count}${filename.slice(dot)}` : `${filename}-${count}`;
}

export function saveBlob(blob: Blob, filename: string): void {
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = href;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}
